<template>
  <div class="job-applications-shell">
    <section class="applications-hero">
      <div class="hero-content">
        <div class="hero-mark" aria-hidden="true">
          <v-icon>mdi-account-tie-outline</v-icon>
        </div>
        <div class="hero-copy">
          <div class="page-eyebrow">Recruiting</div>
          <h1>Bewerbungen</h1>
          <p>Talente zentral prüfen, Zuständigkeiten koordinieren und Entscheidungen transparent nachverfolgen.</p>
        </div>
      </div>
      <div class="hero-actions">
        <span v-if="newCount > 0" class="status-pill status-new">
          <span class="status-dot"></span>
          {{ newCount }} neu
        </span>
        <v-btn
          icon
          class="touch-action refresh-action"
          :loading="loading"
          title="Bewerbungen aktualisieren"
          aria-label="Bewerbungen aktualisieren"
          @click="loadApplications">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </section>

    <section class="kpi-grid" aria-label="Bewerbungsübersicht">
      <v-card dark flat class="kpi-card">
        <div class="kpi-icon kpi-icon-neutral"><v-icon>mdi-format-list-bulleted</v-icon></div>
        <div class="kpi-copy">
          <span>Sichtbare Bewerbungen</span>
          <strong>{{ loading ? "–" : filteredApplications.length }}</strong>
          <small>in der aktuellen Auswahl</small>
        </div>
      </v-card>
      <v-card dark flat class="kpi-card">
        <div class="kpi-icon kpi-icon-new"><v-icon>mdi-sparkles</v-icon></div>
        <div class="kpi-copy">
          <span>Neu</span>
          <strong>{{ loading ? "–" : visibleNewCount }}</strong>
          <small>noch nicht bearbeitet</small>
        </div>
      </v-card>
      <v-card dark flat class="kpi-card">
        <div class="kpi-icon kpi-icon-progress"><v-icon>mdi-progress-clock</v-icon></div>
        <div class="kpi-copy">
          <span>In Bearbeitung</span>
          <strong>{{ loading ? "–" : inProgressCount }}</strong>
          <small>aktuell im Prozess</small>
        </div>
      </v-card>
      <v-card dark flat class="kpi-card">
        <div class="kpi-icon kpi-icon-success"><v-icon>mdi-check-decagram-outline</v-icon></div>
        <div class="kpi-copy">
          <span>Zugesagt</span>
          <strong>{{ loading ? "–" : acceptedCount }}</strong>
          <small>positive Entscheide</small>
        </div>
      </v-card>
    </section>

    <v-card dark flat class="filter-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Ansicht anpassen</div>
          <div class="surface-title-row">
            <h2>Filter &amp; Suche</h2>
            <span class="result-count">{{ loading ? "Wird geladen …" : filteredApplications.length + " Ergebnisse" }}</span>
          </div>
        </div>
      </div>
      <v-row dense class="filter-fields">
        <v-col v-if="canManage" cols="12" sm="6" md="4">
          <v-select
            v-model="filterStudio"
            :items="studioFilterOptions"
            item-text="name"
            item-value="_id"
            label="Studio"
            prepend-inner-icon="mdi-map-marker-outline"
            outlined
            dense
            dark
            hide-details
            class="modern-field"
            @change="loadApplications" />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select
            v-model="filterStatus"
            :items="statusFilterOptions"
            label="Status"
            prepend-inner-icon="mdi-progress-check"
            outlined
            dense
            dark
            hide-details
            class="modern-field"
            @change="loadApplications" />
        </v-col>
        <v-col cols="12" :sm="canManage ? 12 : 6" md="4">
          <v-text-field
            v-model="searchQuery"
            label="Name, E-Mail oder Stelle"
            outlined
            dense
            dark
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            class="modern-field search-field" />
        </v-col>
      </v-row>
    </v-card>

    <v-card dark flat class="applications-surface">
      <div class="applications-header">
        <div>
          <div class="surface-eyebrow">Aktuelle Auswahl</div>
          <h2>Bewerbungseingang</h2>
        </div>
        <div class="status-legend" aria-label="Statuslegende">
          <span><i class="legend-dot legend-new"></i>Neu</span>
          <span><i class="legend-dot legend-progress"></i>In Bearbeitung</span>
          <span><i class="legend-dot legend-success"></i>Zugesagt</span>
          <span><i class="legend-dot legend-rejected"></i>Abgesagt</span>
        </div>
      </div>

      <v-data-table
        v-if="$vuetify.breakpoint.lgAndUp"
        dark
        :headers="tableHeaders"
        :items="filteredApplications"
        :loading="loading"
        :items-per-page="25"
        class="transparent applications-table"
        no-data-text="Keine Bewerbungen vorhanden"
        loading-text="Bewerbungen werden geladen …"
        @click:row="openDetailDialog">
        <template v-slot:item.createdAt="{ item }">
          <div class="date-cell">
            <strong>{{ $moment(item.createdAt).format("DD.MM.YYYY") }}</strong>
            <span>{{ $moment(item.createdAt).format("HH:mm") }} Uhr</span>
          </div>
        </template>
        <template v-slot:item.assignedUsers="{ item }">
          <div class="assigned-cell">
            <v-icon x-small>mdi-account-multiple-outline</v-icon>
            <span>{{
              (item.assignedUsers || []).map((user) => [user.vorname, user.nachname].filter(Boolean).join(" ") || "Unbekannt").join(", ") ||
              "Nicht zugewiesen"
            }}</span>
          </div>
        </template>
        <template v-slot:item.applicant="{ item }">
          <div class="applicant-cell">
            <span class="applicant-avatar">{{ (item.firstName || "?").charAt(0) }}{{ (item.lastName || "").charAt(0) }}</span>
            <div>
              <strong>{{ [item.firstName, item.lastName].filter(Boolean).join(" ") || "Unbekannte Person" }}</strong>
              <span>{{ item.email || "Keine E-Mail" }}</span>
            </div>
          </div>
        </template>
        <template v-slot:item.jobOffer="{ item }">
          <span class="job-cell">{{ item.jobOffer ? item.jobOffer.title || (item.jobOffer.type && item.jobOffer.type.title) || "-" : "-" }}</span>
        </template>
        <template v-slot:item.studio="{ item }">
          <span class="studio-cell"><v-icon x-small>mdi-map-marker-outline</v-icon>{{ item.studio ? item.studio.name : "-" }}</span>
        </template>
        <template v-slot:item.attachments="{ item }">
          <span v-if="item.attachments && item.attachments.length > 0" class="attachment-count">
            <v-icon x-small>mdi-paperclip</v-icon>
            {{ item.attachments.length }}
          </span>
          <span v-else class="empty-value">–</span>
        </template>
        <template v-slot:item.status="{ item }">
          <span class="status-pill" :class="'status-' + item.status">
            <span class="status-dot"></span>
            {{ statusLabel(item.status) }}
          </span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            class="touch-action row-action"
            :aria-label="'Bewerbung von ' + ([item.firstName, item.lastName].filter(Boolean).join(' ') || 'unbekannter Person') + ' ansehen'"
            @click.stop="openDetailDialog(item)">
            <v-icon small>mdi-arrow-right</v-icon>
          </v-btn>
        </template>
        <template v-slot:no-data>
          <div class="empty-state">
            <span class="empty-state-icon"><v-icon>mdi-account-search-outline</v-icon></span>
            <strong>Keine Bewerbungen gefunden</strong>
            <p>Passe Filter oder Suchbegriff an.</p>
          </div>
        </template>
      </v-data-table>

      <v-data-iterator
        v-else
        dark
        :items="filteredApplications"
        :loading="loading"
        :items-per-page="25"
        :footer-props="{ itemsPerPageOptions: [25], showFirstLastPage: true }"
        item-key="_id"
        hide-default-header
        loading-text="Bewerbungen werden geladen …"
        no-data-text="Keine Bewerbungen vorhanden"
        class="applications-mobile-iterator">
        <template v-slot:default="{ items }">
          <div class="application-card-grid">
            <article
              v-for="item in items"
              :key="item._id"
              class="application-card"
              tabindex="0"
              role="button"
              :aria-label="'Bewerbung von ' + ([item.firstName, item.lastName].filter(Boolean).join(' ') || 'unbekannter Person') + ' ansehen'"
              @click="openDetailDialog(item)"
              @keydown.enter.self="openDetailDialog(item)"
              @keydown.space.self.prevent="openDetailDialog(item)">
              <div class="card-topline">
                <span class="received-date">
                  <v-icon x-small>mdi-calendar-blank-outline</v-icon>
                  {{ $moment(item.createdAt).format("DD.MM.YYYY · HH:mm") }}
                </span>
                <span class="status-pill" :class="'status-' + item.status">
                  <span class="status-dot"></span>
                  {{ statusLabel(item.status) }}
                </span>
              </div>
              <div class="card-applicant">
                <span class="applicant-avatar">{{ (item.firstName || "?").charAt(0) }}{{ (item.lastName || "").charAt(0) }}</span>
                <div>
                  <strong>{{ [item.firstName, item.lastName].filter(Boolean).join(" ") || "Unbekannte Person" }}</strong>
                  <span>{{ item.email || "Keine E-Mail" }}</span>
                </div>
              </div>
              <div class="card-facts">
                <div>
                  <v-icon small>mdi-briefcase-outline</v-icon>
                  <span>{{ item.jobOffer ? item.jobOffer.title || (item.jobOffer.type && item.jobOffer.type.title) || "-" : "-" }}</span>
                </div>
                <div>
                  <v-icon small>mdi-map-marker-outline</v-icon>
                  <span>{{ item.studio ? item.studio.name : "-" }}</span>
                </div>
              </div>
              <div class="card-footer">
                <span class="assigned-summary">
                  <v-icon x-small>mdi-account-multiple-outline</v-icon>
                  {{
                    (item.assignedUsers || []).length
                      ? (item.assignedUsers || []).map((user) => [user.vorname, user.nachname].filter(Boolean).join(" ") || "Unbekannt").join(", ")
                      : "Nicht zugewiesen"
                  }}
                </span>
                <div class="card-footer-actions">
                  <span v-if="item.attachments && item.attachments.length" class="attachment-count">
                    <v-icon x-small>mdi-paperclip</v-icon>{{ item.attachments.length }}
                  </span>
                  <v-btn
                    icon
                    class="touch-action row-action"
                    :aria-label="'Bewerbung von ' + ([item.firstName, item.lastName].filter(Boolean).join(' ') || 'unbekannter Person') + ' ansehen'"
                    @click.stop="openDetailDialog(item)">
                    <v-icon small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </article>
          </div>
        </template>
        <template v-slot:no-data>
          <div class="empty-state">
            <span class="empty-state-icon"><v-icon>mdi-account-search-outline</v-icon></span>
            <strong>Keine Bewerbungen gefunden</strong>
            <p>Passe Filter oder Suchbegriff an.</p>
          </div>
        </template>
      </v-data-iterator>
    </v-card>

    <v-dialog
      v-model="detailDialog"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      max-width="1120"
      scrollable
      content-class="job-application-detail-dialog"
      overlay-color="#111112"
      :overlay-opacity="0.78">
      <v-card v-if="currentApplication" dark class="application-detail-dialog">
        <v-card-title class="detail-dialog-header">
          <div class="dialog-mark" aria-hidden="true"><v-icon>mdi-account-tie-outline</v-icon></div>
          <div class="dialog-title-copy">
            <div class="dialog-eyebrow">Bewerbungsdetails</div>
            <h2>{{ [currentApplication.firstName, currentApplication.lastName].filter(Boolean).join(" ") || "Unbekannte Person" }}</h2>
            <p>{{ formatDateTime(currentApplication.createdAt) }} eingegangen</p>
          </div>
          <v-spacer></v-spacer>
          <span class="status-pill detail-status" :class="'status-' + currentApplication.status">
            <span class="status-dot"></span>
            {{ statusLabel(currentApplication.status) }}
          </span>
          <v-btn
            v-if="canEdit(currentApplication)"
            icon
            class="touch-action dialog-action"
            title="Bewerbung bearbeiten"
            aria-label="Bewerbung bearbeiten"
            @click="openEditDialog">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn icon class="touch-action dialog-action" aria-label="Dialog schliessen" @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="detail-dialog-scroll">
          <span class="sr-only" role="status" aria-live="polite">{{ copyFeedback }}</span>

          <div class="detail-overview-grid">
            <section class="detail-card">
              <div class="section-heading">
                <span class="section-icon"><v-icon small>mdi-account-outline</v-icon></span>
                <div>
                  <span>Kontakt</span>
                  <h3>Persönliche Daten</h3>
                </div>
              </div>
              <dl class="detail-list">
                <div v-for="row in contactDetailRows" :key="row.key" class="detail-row">
                  <dt>{{ row.label }}</dt>
                  <dd class="copyable-detail-value">
                    <a v-if="row.href" :href="row.href" class="contact-link">{{ row.value }}</a>
                    <span v-else>{{ row.value }}</span>
                    <v-btn
                      icon
                      type="button"
                      class="detail-copy-action"
                      :class="{ 'detail-copy-action--copied': copiedValueKey === row.key }"
                      :disabled="!hasCopyValue(row.value)"
                      :title="copiedValueKey === row.key ? `${row.label} kopiert` : `${row.label} kopieren`"
                      :aria-label="copiedValueKey === row.key ? `${row.label} kopiert` : `${row.label} kopieren`"
                      @click.stop="copyToClipboard(row.value, row.label, row.key)">
                      <v-icon small>{{ copiedValueKey === row.key ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </dd>
                </div>
              </dl>
            </section>

            <section class="detail-card">
              <div class="section-heading">
                <span class="section-icon"><v-icon small>mdi-briefcase-outline</v-icon></span>
                <div>
                  <span>Position</span>
                  <h3>Bewerbung</h3>
                </div>
              </div>
              <dl class="detail-list">
                <div v-for="row in applicationDetailRows" :key="row.key" class="detail-row">
                  <dt>{{ row.label }}</dt>
                  <dd class="copyable-detail-value">
                    <span>{{ row.value }}</span>
                    <v-btn
                      icon
                      type="button"
                      class="detail-copy-action"
                      :class="{ 'detail-copy-action--copied': copiedValueKey === row.key }"
                      :disabled="!hasCopyValue(row.value)"
                      :title="copiedValueKey === row.key ? `${row.label} kopiert` : `${row.label} kopieren`"
                      :aria-label="copiedValueKey === row.key ? `${row.label} kopiert` : `${row.label} kopieren`"
                      @click.stop="copyToClipboard(row.value, row.label, row.key)">
                      <v-icon small>{{ copiedValueKey === row.key ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                    </v-btn>
                  </dd>
                </div>
              </dl>
            </section>
          </div>

          <section v-if="currentApplication.coverLetter" class="detail-block">
            <div class="block-heading block-heading--copyable">
              <div>
                <span>Motivation</span>
                <h3><v-icon small>mdi-text-box-outline</v-icon>Anschreiben</h3>
              </div>
              <v-btn
                outlined
                type="button"
                class="cover-letter-copy-action"
                :class="{ 'cover-letter-copy-action--copied': copiedValueKey === 'coverLetter' }"
                :title="copiedValueKey === 'coverLetter' ? 'Bewerbungstext kopiert' : 'Bewerbungstext kopieren'"
                :aria-label="copiedValueKey === 'coverLetter' ? 'Bewerbungstext kopiert' : 'Bewerbungstext kopieren'"
                @click="copyToClipboard(currentApplication.coverLetter, 'Bewerbungstext', 'coverLetter')">
                <v-icon left small>{{ copiedValueKey === "coverLetter" ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                {{ copiedValueKey === "coverLetter" ? "Kopiert" : "Text kopieren" }}
              </v-btn>
            </div>
            <div class="cover-letter" tabindex="0">{{ currentApplication.coverLetter }}</div>
          </section>

          <section v-if="detailDialog && currentApplication.attachments && currentApplication.attachments.length > 0" class="detail-block">
            <div class="block-heading">
              <div>
                <span>Dokumente</span>
                <h3><v-icon small>mdi-paperclip</v-icon>Anhänge</h3>
              </div>
              <span class="block-count">{{ currentApplication.attachments.length }}</span>
            </div>
            <div class="attachment-list-shell">
              <attachment-list :application-id="currentApplication._id" :attachments="currentApplication.attachments" />
            </div>
          </section>

          <div class="workflow-grid">
            <section class="detail-block workflow-block">
              <div class="block-heading">
                <div>
                  <span>Verantwortung</span>
                  <h3><v-icon small>mdi-account-multiple-outline</v-icon>Zuständigkeit</h3>
                </div>
              </div>
              <div class="assigned-users">
                <span v-for="user in currentApplication.assignedUsers || []" :key="user._id" class="assigned-user-pill">
                  <v-icon x-small>mdi-account-outline</v-icon>
                  {{ [user.vorname, user.nachname].filter(Boolean).join(" ") || "Unbekannt" }}
                </span>
                <span v-if="!currentApplication.assignedUsers || currentApplication.assignedUsers.length === 0" class="inline-empty">
                  <v-icon small>mdi-account-off-outline</v-icon>Keine Person zugewiesen
                </span>
              </div>
              <v-btn outlined class="forward-action" @click="openForwardDialog"> <v-icon left small>mdi-share-outline</v-icon>Weiterleiten </v-btn>
            </section>

            <section class="detail-block workflow-block">
              <div class="block-heading">
                <div>
                  <span>Prozess</span>
                  <h3><v-icon small>mdi-progress-check</v-icon>Status</h3>
                </div>
              </div>
              <v-btn-toggle v-model="currentApplication.status" mandatory dense class="status-toggle" @change="changeStatus">
                <v-btn value="new"><span class="toggle-dot status-new-dot"></span>Neu</v-btn>
                <v-btn value="in_progress"><span class="toggle-dot status-progress-dot"></span>In Bearbeitung</v-btn>
                <v-btn value="accepted"><span class="toggle-dot status-success-dot"></span>Zugesagt</v-btn>
                <v-btn value="rejected"><span class="toggle-dot status-rejected-dot"></span>Abgesagt</v-btn>
              </v-btn-toggle>
            </section>
          </div>

          <section v-if="currentApplication.forwardHistory && currentApplication.forwardHistory.length > 0" class="detail-block">
            <div class="block-heading">
              <div>
                <span>Verteilung</span>
                <h3><v-icon small>mdi-share-variant-outline</v-icon>Weiterleitungen</h3>
              </div>
              <span class="block-count">{{ currentApplication.forwardHistory.length }}</span>
            </div>
            <div class="forward-list">
              <article v-for="(fwd, idx) in [...currentApplication.forwardHistory].reverse()" :key="idx" class="forward-card">
                <div class="history-meta">
                  <span class="history-avatar"><v-icon small>mdi-share-outline</v-icon></span>
                  <div>
                    <strong>{{ fwd.forwardedBy ? fwd.forwardedBy.vorname + " " + fwd.forwardedBy.nachname : "?" }}</strong>
                    <span>{{ formatDateTime(fwd.forwardedAt) }}</span>
                  </div>
                </div>
                <div class="forward-targets">
                  <span>An</span>
                  <strong v-for="u in fwd.to" :key="u._id">
                    {{ u.vorname }} {{ u.nachname }}{{ fwd.to.indexOf(u) < fwd.to.length - 1 ? "," : "" }}
                  </strong>
                </div>
                <div v-if="fwd.reason" class="forward-reason">„{{ fwd.reason }}“</div>
              </article>
            </div>
          </section>

          <section v-if="currentApplication.editHistory && currentApplication.editHistory.length > 0" class="detail-block">
            <div class="block-heading">
              <div>
                <span>Nachvollziehbarkeit</span>
                <h3><v-icon small>mdi-history</v-icon>Bearbeitungshistorie</h3>
              </div>
              <span class="block-count">{{ currentApplication.editHistory.length }}</span>
            </div>
            <v-expansion-panels dark flat class="history-panels">
              <v-expansion-panel v-for="(h, idx) in [...currentApplication.editHistory].reverse()" :key="idx" class="history-panel">
                <v-expansion-panel-header class="history-panel-header">
                  <div class="history-meta">
                    <span class="history-avatar"><v-icon small>mdi-account-edit-outline</v-icon></span>
                    <div>
                      <strong>{{ h.user ? h.user.vorname + " " + h.user.nachname : "Unbekannt" }}</strong>
                      <span>{{ formatDateTime(h.editedAt) }}</span>
                    </div>
                    <span class="history-action" :class="'history-action-' + h.action">{{ actionLabel(h.action) }}</span>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div v-for="(c, cidx) in h.changes" :key="cidx" class="history-change">
                    <strong>{{ c.field }}</strong>
                    <span class="history-old">{{ formatHistoryValue(c.oldValue) }}</span>
                    <v-icon x-small>mdi-arrow-right</v-icon>
                    <span class="history-new">{{ formatHistoryValue(c.newValue) }}</span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </section>

          <section class="detail-block comments-block">
            <div class="block-heading">
              <div>
                <span>Austausch</span>
                <h3><v-icon small>mdi-comment-multiple-outline</v-icon>Kommentare</h3>
              </div>
              <span class="block-count">{{ currentApplication.comments ? currentApplication.comments.length : 0 }}</span>
            </div>
            <div v-if="currentApplication.comments && currentApplication.comments.length > 0" class="comment-list">
              <article v-for="(c, idx) in currentApplication.comments" :key="idx" class="comment-card">
                <div class="history-meta">
                  <span class="history-avatar"><v-icon small>mdi-account-outline</v-icon></span>
                  <div>
                    <strong>{{ c.user ? c.user.vorname + " " + c.user.nachname : "Unbekannt" }}</strong>
                    <span>{{ formatDateTime(c.createdAt) }}</span>
                  </div>
                </div>
                <div class="comment-text">{{ c.comment }}</div>
              </article>
            </div>
            <div v-else class="inline-empty"><v-icon small>mdi-comment-outline</v-icon>Noch keine Kommentare.</div>

            <v-textarea v-model="newComment" label="Kommentar hinzufügen" outlined dense dark rows="2" class="comment-input" hide-details />
            <div class="comment-submit-row">
              <v-btn
                color="primary"
                depressed
                class="primary-action comment-submit"
                :disabled="!newComment.trim()"
                :loading="savingComment"
                @click="addComment">
                <v-icon left small>mdi-send-outline</v-icon>Kommentar senden
              </v-btn>
            </div>
          </section>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>
        <v-card-actions class="detail-dialog-actions">
          <v-btn v-if="canManage" text color="error" class="delete-action" @click="deleteApplication">
            <v-icon left>mdi-delete-outline</v-icon>Löschen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text class="secondary-action" @click="detailDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EDIT DIALOG (kommt in Teil 2b) -->
    <job-application-edit-dialog
      v-if="editDialog"
      :value="editDialog"
      :application="currentApplication"
      @input="editDialog = $event"
      @saved="handleSaved" />

    <!-- FORWARD DIALOG (kommt in Teil 2b) -->
    <job-application-forward-dialog
      v-if="forwardDialog"
      :value="forwardDialog"
      :application="currentApplication"
      @input="forwardDialog = $event"
      @forwarded="handleForwarded" />
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import socketService from "@/socketService"
import { mapState } from "vuex"

import AttachmentList from "./components/AttachmentList.vue"
import JobApplicationEditDialog from "./components/JobApplicationEditDialog.vue"
import JobApplicationForwardDialog from "./components/JobApplicationForwardDialog.vue"

export default {
  name: "JobApplications",
  components: {
    AttachmentList,
    JobApplicationEditDialog,
    JobApplicationForwardDialog,
  },
  data() {
    return {
      userService,
      loading: false,
      savingComment: false,
      applications: [],
      currentApplication: null,
      detailDialog: false,
      editDialog: false,
      forwardDialog: false,
      newComment: "",
      filterStudio: "all",
      filterStatus: "all",
      searchQuery: "",
      copiedValueKey: null,
      copyFeedback: "",
      copyResetTimer: null,
      tableHeaders: [
        { text: "Eingegangen", value: "createdAt" },
        { text: "Zugewiesen", value: "assignedUsers", sortable: false },
        { text: "Bewerber", value: "applicant", sortable: false },
        { text: "Stelle", value: "jobOffer", sortable: false },
        { text: "Bew. Studio", value: "studio", sortable: false },
        { text: "Anhänge", value: "attachments", sortable: false, align: "center" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      statusFilterOptions: [
        { text: "Alle", value: "all" },
        { text: "Neu", value: "new" },
        { text: "In Bearbeitung", value: "in_progress" },
        { text: "Zugesagt", value: "accepted" },
        { text: "Abgesagt", value: "rejected" },
      ],
      // Bound Socket-Listener-Refs (damit off() korrekt funktioniert)
      socketHandlers: {},
    }
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    canManage() {
      return this.userService.hasRight("d_manageJobApplications")
    },
    studioOptions() {
      return this.storeStudios.filter((s) => s.code && s.code !== "ot" && s.code !== "alibey").map((s) => ({ _id: s._id, name: s.name }))
    },
    studioFilterOptions() {
      return [{ _id: "all", name: "Alle Studios" }, ...this.studioOptions]
    },
    newCount() {
      return this.applications.filter((a) => a.status === "new").length
    },
    visibleNewCount() {
      return this.filteredApplications.filter((application) => application.status === "new").length
    },
    inProgressCount() {
      return this.filteredApplications.filter((application) => application.status === "in_progress").length
    },
    acceptedCount() {
      return this.filteredApplications.filter((application) => application.status === "accepted").length
    },
    contactDetailRows() {
      const application = this.currentApplication || {}
      const name = [application.firstName, application.lastName].filter(Boolean).join(" ") || "-"

      return [
        { key: "contact-name", label: "Name", value: name },
        {
          key: "contact-email",
          label: "E-Mail",
          value: application.email || "-",
          href: application.email ? `mailto:${application.email}` : null,
        },
        {
          key: "contact-phone",
          label: "Telefon",
          value: application.phone || "-",
          href: application.phone ? `tel:${application.phone}` : null,
        },
        { key: "contact-birth-date", label: "Geburtsdatum", value: this.formatDate(application.birthDate) },
        { key: "contact-address", label: "Adresse", value: this.formatAddress(application.address) },
      ]
    },
    applicationDetailRows() {
      const application = this.currentApplication || {}
      const jobTitle = application.jobOffer
        ? application.jobOffer.title || (application.jobOffer.type && application.jobOffer.type.title) || "-"
        : "-"

      return [
        { key: "application-job", label: "Stelle", value: jobTitle },
        {
          key: "application-studio",
          label: "Studio",
          value: (application.studio && application.studio.name) || "-",
        },
        { key: "application-workload", label: "Pensum", value: application.desiredWorkload || "-" },
        {
          key: "application-start-date",
          label: "Frühster Eintritt",
          value: this.formatDate(application.earliestStartDate),
        },
        {
          key: "application-created-at",
          label: "Eingegangen",
          value: this.formatDateTime(application.createdAt),
        },
        {
          key: "application-mail-sent",
          label: "Mail versendet",
          value: application.initialMailSent ? "JA" : "NEIN",
        },
      ]
    },
    filteredApplications() {
      if (!this.searchQuery || !this.searchQuery.trim()) return this.applications
      const q = this.searchQuery.trim().toLowerCase()
      return this.applications.filter((a) => {
        const haystack = [
          a.firstName,
          a.lastName,
          a.email,
          a.phone,
          a.jobOffer && a.jobOffer.title,
          a.jobOffer && a.jobOffer.type && a.jobOffer.type.title,
          a.studio && a.studio.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
        return haystack.includes(q)
      })
    },
  },
  methods: {
    hasCopyValue(value) {
      if (value === null || value === undefined) return false
      const normalizedValue = String(value).trim()
      return normalizedValue.length > 0 && normalizedValue !== "-" && normalizedValue !== "–"
    },
    resetCopyState() {
      if (this.copyResetTimer) clearTimeout(this.copyResetTimer)
      this.copiedValueKey = null
      this.copyFeedback = ""
      this.copyResetTimer = null
    },
    async copyToClipboard(value, label, valueKey) {
      if (!this.hasCopyValue(value)) return

      const text = String(value).trim()
      try {
        if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          try {
            await navigator.clipboard.writeText(text)
          } catch (clipboardError) {
            this.copyWithFallback(text)
          }
        } else {
          this.copyWithFallback(text)
        }

        this.copiedValueKey = valueKey
        this.copyFeedback = `${label} kopiert`
        if (this.$toast) this.$toast.success(`${label} kopiert`)

        if (this.copyResetTimer) clearTimeout(this.copyResetTimer)
        this.copyResetTimer = setTimeout(() => {
          this.copiedValueKey = null
          this.copyFeedback = ""
          this.copyResetTimer = null
        }, 1800)
      } catch (error) {
        this.copiedValueKey = null
        this.copyFeedback = `${label} konnte nicht kopiert werden`
        if (this.$toast) this.$toast.error(`${label} konnte nicht kopiert werden`)
      }
    },
    copyWithFallback(text) {
      const textarea = document.createElement("textarea")
      textarea.value = text
      textarea.setAttribute("readonly", "")
      textarea.style.position = "fixed"
      textarea.style.top = "-9999px"
      textarea.style.opacity = "0"
      document.body.appendChild(textarea)
      textarea.select()

      let copied = false
      try {
        copied = document.execCommand("copy")
      } finally {
        document.body.removeChild(textarea)
      }
      if (!copied) throw new Error("Clipboard API nicht verfügbar")
    },
    async loadApplications() {
      this.loading = true
      try {
        const params = {}
        if (this.canManage && this.filterStudio && this.filterStudio !== "all") params.studio = this.filterStudio
        if (this.filterStatus && this.filterStatus !== "all") params.status = this.filterStatus
        this.applications = await api.jobApplications.fetch(params)
      } catch (e) {
        console.error("Fehler beim Laden:", e)
        this.$toast && this.$toast.error("Bewerbungen konnten nicht geladen werden")
      } finally {
        this.loading = false
      }
    },
    async openDetailDialog(application) {
      this.resetCopyState()
      try {
        this.currentApplication = await api.jobApplications.fetchOne(application._id)
        this.newComment = ""
        this.detailDialog = true
      } catch (e) {
        console.error("Fehler beim Laden des Details:", e)
      }
    },
    openEditDialog() {
      this.editDialog = true
    },
    openForwardDialog() {
      this.forwardDialog = true
    },
    handleSaved(updated) {
      this.currentApplication = updated
      this.editDialog = false
      this.loadApplications()
    },
    handleForwarded(updated) {
      this.currentApplication = updated
      this.forwardDialog = false
      this.loadApplications()
    },
    async changeStatus(status) {
      if (!this.currentApplication) return
      try {
        const updated = await api.jobApplications.updateStatus(this.currentApplication._id, status)
        this.currentApplication = updated
        this.loadApplications()
      } catch (e) {
        console.error("Fehler beim Statuswechsel:", e)
        this.$toast && this.$toast.error("Status konnte nicht geändert werden")
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.currentApplication) return
      this.savingComment = true
      try {
        const updated = await api.jobApplications.addComment(this.currentApplication._id, this.newComment)
        this.currentApplication = updated
        this.newComment = ""
      } catch (e) {
        console.error("Fehler beim Kommentar:", e)
      } finally {
        this.savingComment = false
      }
    },
    async deleteApplication() {
      if (!this.currentApplication) return
      if (!confirm("Bewerbung wirklich löschen? Alle Anhänge werden ebenfalls entfernt.")) return
      try {
        await api.jobApplications.delete(this.currentApplication._id)
        this.detailDialog = false
        this.loadApplications()
        this.$toast && this.$toast.success("Bewerbung gelöscht")
      } catch (e) {
        console.error("Fehler beim Löschen:", e)
      }
    },
    canEdit(application) {
      return application && application.status !== "accepted" && application.status !== "rejected"
    },
    formatDate(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY") : "-"
    },
    formatDateTime(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY HH:mm") : "-"
    },
    formatAddress(addr) {
      if (!addr) return "-"
      const parts = []
      if (addr.street) parts.push(addr.street)
      const cityLine = [addr.zip, addr.city].filter(Boolean).join(" ")
      if (cityLine) parts.push(cityLine)
      return parts.length > 0 ? parts.join(", ") : "-"
    },
    formatHistoryValue(val) {
      if (val === null || val === undefined || val === "") return "-"
      if (Array.isArray(val)) return val.length + " Einträge"
      if (typeof val === "object") {
        if (val.vorname) return `${val.vorname} ${val.nachname || ""}`.trim()
        if (val.name) return val.name
        return JSON.stringify(val)
      }
      return String(val)
    },
    statusColor(s) {
      if (s === "accepted") return "green"
      if (s === "in_progress") return "orange"
      if (s === "rejected") return "red darken-2"
      return "grey darken-1"
    },
    statusLabel(s) {
      if (s === "accepted") return "Zugesagt"
      if (s === "in_progress") return "In Bearbeitung"
      if (s === "rejected") return "Abgesagt"
      return "Neu"
    },
    actionColor(a) {
      if (a === "forwarded") return "orange"
      if (a === "status_change") return "green darken-1"
      return "grey darken-1"
    },
    actionLabel(a) {
      if (a === "forwarded") return "Weitergeleitet"
      if (a === "status_change") return "Status"
      if (a === "edit") return "Bearbeitet"
      return a || "Aktion"
    },
    // ---------- WebSocket ----------
    setupSocketListeners() {
      this.socketHandlers.onNew = (payload) => {
        // Toast-Hinweis und Liste neu laden
        const name = payload.applicantName || "Neue Bewerbung"
        if (this.$toast) {
          if (payload.forwarded) {
            this.$toast.info(`Bewerbung weitergeleitet: ${name}`)
          } else {
            this.$toast.success(`Neue Bewerbung: ${name}`)
          }
        }
        this.loadApplications()
      }

      this.socketHandlers.onUpdated = (payload) => {
        // Wenn Detail-Dialog gerade offen ist und genau diese Bewerbung betrifft → reload
        if (this.detailDialog && this.currentApplication && this.currentApplication._id === payload.applicationId) {
          this.refreshCurrent()
        }
        this.loadApplications()
      }

      this.socketHandlers.onCommented = (payload) => {
        if (this.detailDialog && this.currentApplication && this.currentApplication._id === payload.applicationId) {
          this.refreshCurrent()
        }
      }

      this.socketHandlers.onRemoved = (payload) => {
        // Falls aktuell offene Bewerbung weitergeleitet wurde → Dialog schliessen
        if (this.detailDialog && this.currentApplication && this.currentApplication._id === payload.applicationId) {
          this.detailDialog = false
          this.$toast && this.$toast.info("Diese Bewerbung wurde an jemand anderen weitergeleitet")
        }
        this.loadApplications()
      }

      socketService.off("jobApplication:new", this.socketHandlers.onNew)
      socketService.on("jobApplication:new", this.socketHandlers.onNew)

      socketService.off("jobApplication:updated", this.socketHandlers.onUpdated)
      socketService.on("jobApplication:updated", this.socketHandlers.onUpdated)

      socketService.off("jobApplication:commented", this.socketHandlers.onCommented)
      socketService.on("jobApplication:commented", this.socketHandlers.onCommented)

      socketService.off("jobApplication:removed", this.socketHandlers.onRemoved)
      socketService.on("jobApplication:removed", this.socketHandlers.onRemoved)
    },
    teardownSocketListeners() {
      socketService.off("jobApplication:new", this.socketHandlers.onNew)
      socketService.off("jobApplication:updated", this.socketHandlers.onUpdated)
      socketService.off("jobApplication:commented", this.socketHandlers.onCommented)
      socketService.off("jobApplication:removed", this.socketHandlers.onRemoved)
    },
    async refreshCurrent() {
      if (!this.currentApplication) return
      try {
        this.currentApplication = await api.jobApplications.fetchOne(this.currentApplication._id)
      } catch (e) {
        console.error("Refresh fehlgeschlagen:", e)
      }
    },
  },
  created() {
    this.loadApplications()
    this.setupSocketListeners()
  },
  beforeDestroy() {
    if (this.copyResetTimer) clearTimeout(this.copyResetTimer)
    this.teardownSocketListeners()
  },
}
</script>

<style scoped>
.job-applications-shell {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --surface-hover: rgba(255, 255, 255, 0.07);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  --primary: #009640;
  --primary-soft: #58d388;
  max-width: 1480px;
  margin: 0 auto;
  padding: 24px;
  color: var(--text);
}

.applications-hero {
  position: relative;
  display: flex;
  min-height: 190px;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding: 34px 38px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 24px;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.08), transparent 48%), rgba(255, 255, 255, 0.035);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.applications-hero::after {
  position: absolute;
  right: 9%;
  bottom: -175px;
  width: 270px;
  height: 270px;
  border-radius: 50%;
  background: rgba(0, 150, 64, 0.1);
  content: "";
  filter: blur(22px);
  pointer-events: none;
}

.hero-content,
.hero-actions,
.applicant-cell,
.assigned-cell,
.studio-cell,
.card-topline,
.card-footer,
.card-footer-actions,
.history-meta,
.forward-targets,
.assigned-users,
.inline-empty {
  display: flex;
  align-items: center;
}

.hero-content {
  position: relative;
  z-index: 1;
  min-width: 0;
  gap: 20px;
}

.hero-mark,
.dialog-mark,
.kpi-icon,
.applicant-avatar,
.empty-state-icon,
.section-icon,
.history-avatar {
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
}

.hero-mark {
  width: 58px;
  height: 58px;
  border: 1px solid rgba(0, 150, 64, 0.28);
  border-radius: 17px;
  color: var(--primary-soft);
  background: rgba(0, 150, 64, 0.13);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.hero-mark .v-icon {
  color: currentColor;
  font-size: 29px;
}

.hero-copy {
  min-width: 0;
}

.page-eyebrow,
.surface-eyebrow,
.dialog-eyebrow,
.section-heading > div > span,
.block-heading > div > span {
  color: var(--primary-soft);
  font-size: 10px;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.hero-copy h1 {
  margin: 5px 0 7px;
  color: #ffffff;
  font-size: clamp(30px, 4vw, 44px);
  font-weight: 760;
  line-height: 1.08;
  letter-spacing: -0.035em;
}

.hero-copy p {
  max-width: 690px;
  margin: 0;
  color: #b8b8bc;
  font-size: 15px;
  line-height: 1.6;
}

.hero-actions {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  gap: 10px;
}

.touch-action {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
  border: 1px solid var(--border);
  border-radius: 13px !important;
  color: var(--text-soft) !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.refresh-action:hover,
.dialog-action:hover {
  color: var(--text) !important;
  background: rgba(255, 255, 255, 0.09) !important;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.kpi-card {
  display: flex;
  min-width: 0;
  min-height: 108px;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border: 1px solid var(--border);
  border-radius: 19px !important;
  background: var(--surface) !important;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.kpi-icon {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.kpi-icon .v-icon {
  color: currentColor;
  font-size: 20px;
}

.kpi-icon-new {
  color: #ededee;
  background: rgba(255, 255, 255, 0.08);
}

.kpi-icon-progress {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.kpi-icon-success {
  color: var(--primary-soft);
  background: rgba(0, 150, 64, 0.11);
}

.kpi-copy {
  display: grid;
  min-width: 0;
}

.kpi-copy > span {
  overflow: hidden;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-copy strong {
  color: #ffffff;
  font-size: 27px;
  font-weight: 760;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.kpi-copy small {
  margin-top: 3px;
  overflow: hidden;
  color: var(--muted);
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-surface,
.applications-surface {
  border: 1px solid var(--border);
  border-radius: 22px !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.filter-surface {
  margin-bottom: 16px;
  padding: 20px 22px 14px;
}

.surface-header,
.applications-header,
.surface-title-row {
  display: flex;
  align-items: center;
}

.surface-header,
.applications-header {
  justify-content: space-between;
  gap: 18px;
}

.surface-title-row {
  gap: 11px;
  margin-top: 3px;
}

.surface-title-row h2,
.applications-header h2 {
  margin: 0;
  color: var(--text);
  font-size: 20px;
  font-weight: 740;
  letter-spacing: -0.02em;
}

.result-count,
.block-count,
.attachment-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  font-weight: 700;
  white-space: nowrap;
}

.result-count {
  min-height: 25px;
  padding: 3px 9px;
  font-size: 11px;
}

.filter-fields {
  margin-top: 10px;
}

.modern-field ::v-deep .v-input__slot,
.comment-input ::v-deep .v-input__slot {
  min-height: 46px !important;
  border-radius: 13px !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset,
.comment-input ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.comment-input ::v-deep .v-label {
  color: #ababaf !important;
}

.modern-field ::v-deep input {
  color: var(--text) !important;
}

.modern-field.v-input--is-focused ::v-deep fieldset,
.comment-input.v-input--is-focused ::v-deep fieldset {
  border-color: #009640 !important;
}

.modern-field.v-input--is-focused ::v-deep .v-icon,
.comment-input.v-input--is-focused ::v-deep .v-icon {
  color: var(--primary-soft) !important;
}

.applications-surface {
  padding-top: 22px;
  overflow: hidden;
}

.applications-header {
  padding: 0 22px 17px;
}

.status-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--muted);
  font-size: 10px;
}

.status-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-dot,
.status-dot,
.toggle-dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.legend-new,
.status-new .status-dot,
.status-new-dot {
  background: #c5c5c8;
}

.legend-progress,
.status-in_progress .status-dot,
.status-progress-dot {
  background: #fbbf24;
}

.legend-success,
.status-accepted .status-dot,
.status-success-dot {
  background: #58d388;
}

.legend-rejected,
.status-rejected .status-dot,
.status-rejected-dot {
  background: #fb7185;
}

.status-pill {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  gap: 7px;
  padding: 4px 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 750;
  line-height: 1;
  white-space: nowrap;
}

.status-new {
  border-color: rgba(255, 255, 255, 0.12);
  color: #dddddf;
  background: rgba(255, 255, 255, 0.065);
}

.status-in_progress {
  border-color: rgba(251, 191, 36, 0.2);
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.1);
}

.status-accepted {
  border-color: rgba(0, 150, 64, 0.27);
  color: #66d691;
  background: rgba(0, 150, 64, 0.11);
}

.status-rejected {
  border-color: rgba(251, 113, 133, 0.2);
  color: #fda4af;
  background: rgba(244, 63, 94, 0.1);
}

.applications-table ::v-deep .v-data-table__wrapper {
  border-top: 1px solid var(--border);
}

.applications-table ::v-deep thead th {
  height: 48px;
  border-bottom-color: var(--border) !important;
  color: #8f8f93 !important;
  background: rgba(0, 0, 0, 0.13);
  font-size: 9px !important;
  font-weight: 800 !important;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.applications-table ::v-deep tbody tr {
  cursor: pointer;
  transition: background-color 150ms ease;
}

.applications-table ::v-deep tbody tr:hover {
  background: rgba(255, 255, 255, 0.045) !important;
}

.applications-table ::v-deep tbody td {
  height: 72px;
  border-bottom-color: rgba(255, 255, 255, 0.07) !important;
  color: #dddddf;
  font-size: 12px;
}

.applications-table ::v-deep .v-data-footer,
.applications-mobile-iterator ::v-deep .v-data-footer {
  min-height: 60px;
  border-top: 1px solid var(--border);
  color: var(--muted);
  background: rgba(0, 0, 0, 0.12);
}

.applications-table ::v-deep .v-progress-linear .primary {
  background-color: #009640 !important;
  border-color: #009640 !important;
}

.date-cell {
  display: grid;
  gap: 2px;
}

.date-cell strong {
  color: #eeeeef;
  font-size: 12px;
}

.date-cell span,
.applicant-cell span,
.empty-value {
  color: var(--muted);
  font-size: 10px;
}

.assigned-cell {
  max-width: 190px;
  gap: 6px;
  color: #b8b8bc;
}

.assigned-cell .v-icon,
.studio-cell .v-icon {
  color: #8f8f93;
}

.assigned-cell span,
.job-cell,
.studio-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.applicant-cell {
  gap: 10px;
  min-width: 0;
}

.applicant-avatar {
  width: 36px;
  height: 36px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 11px;
  color: #e4e4e7;
  background: rgba(255, 255, 255, 0.065);
  font-size: 10px;
  font-weight: 800;
}

.applicant-cell > div,
.card-applicant > div {
  display: grid;
  min-width: 0;
}

.applicant-cell strong {
  overflow: hidden;
  color: var(--text);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.job-cell {
  display: block;
  max-width: 190px;
  color: #d4d4d8;
}

.studio-cell {
  gap: 5px;
  max-width: 150px;
  color: #b8b8bc;
}

.attachment-count {
  min-height: 26px;
  gap: 4px;
  padding: 3px 8px;
  font-size: 10px;
}

.row-action {
  color: var(--primary-soft) !important;
  background: rgba(0, 150, 64, 0.11) !important;
  border-color: rgba(0, 150, 64, 0.24);
}

.empty-state {
  display: flex;
  min-height: 240px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 34px 20px;
  color: var(--muted);
  text-align: center;
}

.empty-state-icon {
  width: 54px;
  height: 54px;
  margin-bottom: 13px;
  border: 1px solid var(--border);
  border-radius: 16px;
  color: #b8b8bc;
  background: rgba(255, 255, 255, 0.055);
}

.empty-state-icon .v-icon {
  color: currentColor;
}

.empty-state strong {
  color: #e4e4e7;
  font-size: 14px;
}

.empty-state p {
  margin: 5px 0 0;
  font-size: 11px;
}

.applications-mobile-iterator {
  border-top: 1px solid var(--border);
  background: transparent !important;
}

.application-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 14px;
}

.application-card {
  min-width: 0;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 17px;
  outline: none;
  background: rgba(255, 255, 255, 0.04);
  transition: transform 150ms ease, border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease;
}

.application-card:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.card-topline,
.card-footer {
  justify-content: space-between;
  gap: 10px;
}

.received-date {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--muted);
  font-size: 10px;
  font-weight: 650;
}

.received-date .v-icon {
  color: #8f8f93;
}

.card-applicant {
  display: flex;
  align-items: center;
  gap: 11px;
  margin: 17px 0 15px;
}

.card-applicant .applicant-avatar {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  font-size: 11px;
}

.card-applicant strong {
  overflow: hidden;
  color: var(--text);
  font-size: 15px;
  font-weight: 740;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-applicant span {
  margin-top: 2px;
  overflow: hidden;
  color: var(--muted);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-facts {
  display: grid;
  gap: 8px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 13px;
  background: rgba(0, 0, 0, 0.12);
}

.card-facts > div {
  display: grid;
  min-width: 0;
  grid-template-columns: 20px minmax(0, 1fr);
  align-items: center;
  gap: 6px;
}

.card-facts .v-icon {
  color: #8f8f93;
}

.card-facts span {
  overflow: hidden;
  color: #c7c7cb;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-footer {
  margin-top: 13px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.assigned-summary {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 5px;
  overflow: hidden;
  color: var(--muted);
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assigned-summary .v-icon {
  flex: 0 0 auto;
  color: #8f8f93;
}

.card-footer-actions {
  flex: 0 0 auto;
  gap: 7px;
}

.application-detail-dialog {
  --dialog-text: #f4f4f5;
  --dialog-soft: #d4d4d8;
  --dialog-muted: #a3a3a8;
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  --border: rgba(255, 255, 255, 0.1);
  --primary-soft: #58d388;
  color: var(--dialog-text);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px !important;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), rgba(28, 28, 29, 0.985) !important;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.54);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.detail-dialog-header {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  min-height: 96px;
  padding: 18px 22px !important;
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
  white-space: normal;
}

.dialog-mark {
  width: 48px;
  height: 48px;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 14px;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.dialog-mark .v-icon {
  color: currentColor;
}

.dialog-title-copy {
  min-width: 0;
}

.dialog-title-copy h2 {
  margin: 2px 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 21px;
  font-weight: 750;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialog-title-copy p {
  margin: 0;
  color: var(--dialog-muted);
  font-size: 11px;
  line-height: 1.4;
}

.detail-status {
  align-self: center;
  margin-right: 3px;
}

.dialog-divider {
  border-color: rgba(255, 255, 255, 0.085) !important;
}

.detail-dialog-scroll {
  max-height: 76vh;
  padding: 20px 22px 24px !important;
}

.detail-overview-grid,
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.detail-card,
.detail-block {
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.035);
}

.detail-card {
  padding: 17px;
}

.detail-block {
  margin-top: 12px;
  padding: 17px;
}

.section-heading,
.block-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.block-heading {
  justify-content: space-between;
}

.section-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.06);
}

.section-icon .v-icon {
  color: currentColor;
}

.section-heading > div,
.block-heading > div {
  display: grid;
  min-width: 0;
}

.section-heading > div > span,
.block-heading > div > span {
  color: #8f8f93;
  font-size: 8px;
}

.section-heading h3,
.block-heading h3 {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 1px 0 0;
  color: #eeeeef;
  font-size: 14px;
  font-weight: 730;
}

.section-heading h3 .v-icon,
.block-heading h3 .v-icon {
  color: #b8b8bc;
}

.detail-list {
  margin: 0;
}

.detail-row {
  display: grid;
  grid-template-columns: minmax(112px, 0.72fr) minmax(0, 1.28fr);
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.065);
  font-size: 12px;
  line-height: 1.45;
}

.detail-row:first-child {
  padding-top: 0;
  border-top: 0;
}

.detail-row:last-child {
  padding-bottom: 0;
}

.detail-row dt {
  color: #929296;
}

.detail-row dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  color: #dddddf;
  font-weight: 600;
}

.copyable-detail-value {
  display: flex;
  align-items: center;
  gap: 7px;
}

.copyable-detail-value > a,
.copyable-detail-value > span {
  min-width: 0;
  flex: 1 1 auto;
  overflow-wrap: anywhere;
}

.detail-copy-action {
  flex: 0 0 auto;
  width: 36px !important;
  min-width: 36px !important;
  height: 36px !important;
  margin: -5px 0;
  border-radius: 10px;
  color: #96969b !important;
}

.detail-copy-action:not(.v-btn--disabled):hover,
.detail-copy-action:not(.v-btn--disabled):focus-visible {
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.14);
}

.detail-copy-action--copied {
  color: #65ca8b !important;
  background: rgba(0, 150, 64, 0.14);
}

.contact-link {
  color: #66d691 !important;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
}

.cover-letter,
.forward-reason,
.comment-text {
  white-space: pre-wrap;
}

.cover-letter {
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 13px;
  color: #c7c7cb;
  background: rgba(0, 0, 0, 0.13);
  font-size: 12px;
  line-height: 1.7;
  overflow-wrap: anywhere;
  cursor: text;
  user-select: text;
}

.cover-letter:focus-visible {
  outline: 2px solid rgba(101, 202, 139, 0.9);
  outline-offset: 2px;
}

.cover-letter-copy-action {
  flex: 0 0 auto;
  min-height: 40px;
  margin-left: auto;
  border-color: rgba(0, 150, 64, 0.34) !important;
  border-radius: 12px;
  color: #d8f3e2 !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.cover-letter-copy-action:hover,
.cover-letter-copy-action:focus-visible,
.cover-letter-copy-action--copied {
  border-color: rgba(0, 150, 64, 0.54) !important;
  color: #ffffff !important;
  background: rgba(0, 150, 64, 0.14);
}

.block-count {
  min-width: 25px;
  min-height: 25px;
  padding: 3px 8px;
  font-size: 10px;
}

.attachment-list-shell {
  overflow: hidden;
  border-radius: 13px;
}

.attachment-list-shell ::v-deep .v-btn {
  min-width: 44px;
  min-height: 44px;
}

.workflow-grid {
  margin-top: 12px;
}

.workflow-grid .detail-block {
  margin-top: 0;
}

.workflow-block {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.assigned-users {
  flex-wrap: wrap;
  gap: 7px;
}

.assigned-user-pill {
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border: 1px solid rgba(0, 150, 64, 0.22);
  border-radius: 999px;
  color: #66d691;
  background: rgba(0, 150, 64, 0.09);
  font-size: 10px;
  font-weight: 700;
}

.assigned-user-pill .v-icon {
  color: currentColor;
}

.inline-empty {
  gap: 7px;
  min-height: 44px;
  padding: 9px 11px;
  border: 1px dashed rgba(255, 255, 255, 0.11);
  border-radius: 12px;
  color: #929296;
  background: rgba(255, 255, 255, 0.025);
  font-size: 11px;
}

.forward-action {
  align-self: flex-start;
  min-height: 44px;
  margin-top: 13px;
  border-color: rgba(245, 158, 11, 0.28) !important;
  border-radius: 12px !important;
  color: #fcd34d !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.status-toggle {
  display: grid !important;
  width: 100%;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 13px !important;
  background: rgba(0, 0, 0, 0.18) !important;
}

.status-toggle ::v-deep .v-btn {
  min-width: 0 !important;
  min-height: 44px;
  justify-content: flex-start;
  padding: 0 10px !important;
  border: 0 !important;
  border-radius: 9px !important;
  color: #a3a3a8 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.status-toggle ::v-deep .v-btn--active {
  color: #f4f4f5 !important;
  background: rgba(255, 255, 255, 0.09) !important;
}

.toggle-dot {
  margin-right: 7px;
}

.forward-list,
.comment-list {
  display: grid;
  gap: 8px;
}

.forward-card,
.comment-card,
.history-panel {
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.03);
}

.forward-card,
.comment-card {
  padding: 13px;
}

.history-meta {
  min-width: 0;
  gap: 9px;
}

.history-avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.06);
}

.history-avatar .v-icon {
  color: currentColor;
}

.history-meta > div {
  display: grid;
  min-width: 0;
  flex: 1;
}

.history-meta strong {
  overflow: hidden;
  color: #dddddf;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-meta span {
  color: #8f8f93;
  font-size: 9px;
}

.forward-targets {
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 10px;
  padding-top: 9px;
  border-top: 1px solid rgba(255, 255, 255, 0.065);
  font-size: 10px;
}

.forward-targets > span {
  color: #8f8f93;
}

.forward-targets strong {
  color: #c7c7cb;
}

.forward-reason {
  margin-top: 9px;
  color: #b8b8bc;
  font-size: 11px;
  font-style: italic;
  line-height: 1.55;
}

.history-panels {
  gap: 7px;
}

.history-panel {
  overflow: hidden;
  background: rgba(255, 255, 255, 0.03) !important;
}

.history-panel::before {
  box-shadow: none !important;
}

.history-panel-header {
  min-height: 56px !important;
}

.history-panel-header .history-meta {
  width: 100%;
}

.history-action {
  flex: 0 0 auto !important;
  padding: 3px 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  color: #b8b8bc !important;
  background: rgba(255, 255, 255, 0.05);
  font-size: 8px !important;
  font-weight: 700;
}

.history-action-forwarded {
  border-color: rgba(245, 158, 11, 0.2);
  color: #fcd34d !important;
  background: rgba(245, 158, 11, 0.08);
}

.history-action-status_change {
  border-color: rgba(0, 150, 64, 0.22);
  color: #66d691 !important;
  background: rgba(0, 150, 64, 0.08);
}

.history-change {
  display: grid;
  grid-template-columns: minmax(100px, 0.7fr) minmax(0, 1fr) 18px minmax(0, 1fr);
  align-items: center;
  gap: 7px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.065);
  font-size: 10px;
}

.history-change:last-child {
  border-bottom: 0;
}

.history-change > strong {
  color: #d4d4d8;
}

.history-old {
  color: #fda4af;
}

.history-new {
  color: #66d691;
}

.history-change .v-icon {
  color: #8f8f93;
}

.comment-card {
  display: grid;
  gap: 10px;
}

.comment-text {
  color: #c7c7cb;
  font-size: 11px;
  line-height: 1.6;
}

.comment-input {
  margin-top: 12px !important;
}

.comment-submit-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
}

.primary-action,
.secondary-action,
.delete-action {
  min-height: 44px !important;
  border-radius: 12px !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action:not(.v-btn--disabled) {
  color: #ffffff !important;
  background-color: #009640 !important;
  box-shadow: 0 9px 22px rgba(0, 150, 64, 0.15);
}

.detail-dialog-actions {
  min-height: 70px;
  gap: 8px;
  padding: 12px 22px !important;
  background: rgba(20, 20, 21, 0.92);
}

.secondary-action {
  color: #d4d4d8 !important;
}

.refresh-action:focus-visible,
.application-card:focus-visible,
.row-action:focus-visible,
.dialog-action:focus-visible,
.detail-copy-action:focus-visible,
.cover-letter-copy-action:focus-visible,
.forward-action:focus-visible,
.status-toggle ::v-deep .v-btn:focus-visible,
.primary-action:focus-visible,
.secondary-action:focus-visible,
.delete-action:focus-visible {
  outline: 2px solid #58d388;
  outline-offset: 2px;
}

.transparent {
  background: transparent !important;
}

@media (hover: hover) and (pointer: fine) {
  .kpi-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }

  .application-card:hover {
    transform: translateY(-1px);
    border-color: var(--border-strong);
    background: rgba(255, 255, 255, 0.055);
  }

  .row-action:hover {
    color: #70d99a !important;
    border-color: rgba(0, 150, 64, 0.32);
    background: rgba(0, 150, 64, 0.17) !important;
  }
}

@media (max-width: 1263px) {
  .job-applications-shell {
    padding: 18px;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .status-legend {
    display: none;
  }
}

@media (max-width: 959px) {
  .applications-hero {
    min-height: 172px;
    padding: 28px;
  }

  .application-detail-dialog {
    border: 0;
    border-radius: 0 !important;
  }

  .detail-overview-grid,
  .workflow-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .workflow-grid .detail-block + .detail-block {
    margin-top: 0;
  }

  .detail-dialog-scroll {
    max-height: none;
  }
}

@media (max-width: 599px) {
  .job-applications-shell {
    padding: 12px;
  }

  .applications-hero {
    align-items: flex-start;
    min-height: auto;
    padding: 21px 18px 18px;
    border-radius: 20px;
  }

  .hero-content {
    align-items: flex-start;
    gap: 12px;
  }

  .hero-mark {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }

  .hero-mark .v-icon {
    font-size: 23px;
  }

  .page-eyebrow {
    font-size: 8px;
  }

  .hero-copy h1 {
    margin-top: 3px;
    font-size: 27px;
  }

  .hero-copy p {
    margin-top: 8px;
    font-size: 11px;
    line-height: 1.55;
  }

  .hero-actions {
    flex-direction: column-reverse;
  }

  .hero-actions .status-pill {
    position: absolute;
    right: 0;
    bottom: -47px;
  }

  .kpi-grid {
    gap: 7px;
    margin: 10px 0;
  }

  .kpi-card {
    display: grid;
    min-height: 110px;
    align-content: start;
    gap: 7px;
    padding: 11px 10px;
    border-radius: 15px !important;
  }

  .kpi-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }

  .kpi-copy > span {
    font-size: 9px;
    white-space: normal;
  }

  .kpi-copy strong {
    font-size: 22px;
  }

  .kpi-copy small {
    display: none;
  }

  .filter-surface {
    margin-bottom: 10px;
    padding: 17px 14px 10px;
    border-radius: 18px !important;
  }

  .surface-title-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .surface-title-row h2,
  .applications-header h2 {
    font-size: 18px;
  }

  .applications-surface {
    padding-top: 18px;
    border-radius: 18px !important;
  }

  .applications-header {
    padding: 0 15px 14px;
  }

  .application-card-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 9px;
    padding: 10px;
  }

  .application-card {
    padding: 14px;
  }

  .applications-mobile-iterator ::v-deep .v-data-footer {
    justify-content: center;
    padding: 8px;
  }

  .applications-mobile-iterator ::v-deep .v-data-footer__select {
    display: none;
  }

  .application-detail-dialog {
    border: 0;
    border-radius: 0 !important;
  }

  .detail-dialog-header {
    flex-wrap: wrap;
    gap: 10px;
    min-height: 88px;
    padding: 14px !important;
  }

  .dialog-mark {
    width: 42px;
    height: 42px;
    border-radius: 12px;
  }

  .dialog-title-copy {
    flex: 1 1 calc(100% - 104px);
  }

  .dialog-title-copy h2 {
    font-size: 17px;
  }

  .detail-dialog-header > .spacer {
    display: none;
  }

  .detail-status {
    margin-right: auto;
  }

  .detail-dialog-scroll {
    padding: 14px !important;
  }

  .detail-card,
  .detail-block {
    padding: 14px;
    border-radius: 14px;
  }

  .detail-row {
    grid-template-columns: minmax(92px, 0.62fr) minmax(0, 1.38fr);
    gap: 8px;
  }

  .block-heading--copyable {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .cover-letter-copy-action {
    width: 100%;
    margin-left: 0;
  }

  .status-toggle {
    grid-template-columns: minmax(0, 1fr);
  }

  .history-panel-header {
    padding: 12px !important;
  }

  .history-meta {
    flex-wrap: wrap;
  }

  .history-action {
    margin-left: 41px;
  }

  .history-change {
    grid-template-columns: minmax(80px, 1fr) minmax(0, 1fr);
  }

  .history-change .v-icon {
    display: none;
  }

  .history-old,
  .history-new {
    grid-column: 2;
  }

  .comment-submit {
    width: 100%;
  }

  .detail-dialog-actions {
    padding: 11px 14px !important;
  }
}

@media (max-width: 379px) {
  .hero-copy p {
    display: none;
  }

  .card-topline {
    align-items: flex-start;
  }

  .received-date {
    max-width: 46%;
  }

  .detail-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 3px;
  }
}
</style>
